import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Copy, Download, Smartphone, ScanLine, KeyRound, ChevronRight, ChevronLeft, Plus } from "lucide-react";

const inp = "w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-center font-mono text-2xl tracking-[0.4em] outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20";
const btn = "flex w-full items-center justify-center gap-2 rounded-xl bg-vino-700 px-4 py-3.5 text-base font-semibold text-white hover:bg-vino-800 disabled:opacity-60";
const sec = "flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50";
const PLAY = "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2";
const APPLE = "https://apps.apple.com/app/google-authenticator/id388497605";
const solo6 = (v) => v.replace(/\D/g, "").slice(0, 6);

function Pasos({ actual }) {
  return (
    <div className="flex items-center gap-2" data-testid="mfa-pasos">
      {[1, 2, 3].map((n) => (
        <div key={n} className={`h-2 flex-1 rounded-full ${n <= actual ? "bg-vino-700" : "bg-slate-200"}`} />
      ))}
      <span className="ml-1 text-xs font-semibold text-slate-500">Paso {actual} de 3</span>
    </div>
  );
}

function Error({ msg }) {
  return msg ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="mfa-error">{msg}</p> : null;
}

function Codigos({ codigos, onSeguir }) {
  const [ok, setOk] = useState(false);
  const txt = `Códigos de recuperación · Roge by Human RMSA\nCada código sirve una sola vez.\n\n${codigos.join("\n")}`;
  const bajar = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([txt])); a.download = "roge-codigos-recuperacion.txt"; a.click(); };
  return (
    <div className="space-y-4" data-testid="mfa-codigos">
      <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">¡Listo! La verificación quedó activada.</p>
      <p className="text-sm text-slate-600">Último paso: guarda estos códigos de emergencia. Si pierdes el celular, cada uno te deja entrar <strong>una sola vez</strong>. Roge no los volverá a mostrar.</p>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 font-mono text-sm text-slate-800">{codigos.map((c) => <span key={c} data-testid="mfa-codigo-item">{c}</span>)}</div>
      <div className="flex gap-2">
        <button type="button" onClick={() => { navigator.clipboard.writeText(txt); toast.success("Códigos copiados"); }} className={sec} data-testid="mfa-copiar-codigos"><Copy className="h-4 w-4" /> Copiar</button>
        <button type="button" onClick={bajar} className={sec} data-testid="mfa-descargar-codigos"><Download className="h-4 w-4" /> Descargar</button>
      </div>
      <label className="flex items-center gap-3 rounded-xl border-2 border-slate-200 p-3 text-sm text-slate-700"><input type="checkbox" className="h-5 w-5" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="mfa-codigos-guardados" /> Ya guardé mis códigos</label>
      <button disabled={!ok} onClick={onSeguir} className={btn} data-testid="mfa-continuar">Entrar a Roge <ChevronRight className="h-5 w-5" /></button>
    </div>
  );
}

function Instalar({ onSiguiente }) {
  return (
    <div className="space-y-4" data-testid="mfa-paso-instalar">
      <div className="flex gap-3"><Smartphone className="h-8 w-8 shrink-0 text-vino-700" /><p className="text-base text-slate-700">Instala en tu celular la app gratuita <strong>Google Authenticator</strong>. Te dará un número nuevo cada 30 segundos para entrar a Roge.</p></div>
      <div className="grid grid-cols-2 gap-2">
        <a href={PLAY} target="_blank" rel="noreferrer" className={sec} data-testid="mfa-link-android">Celular Android</a>
        <a href={APPLE} target="_blank" rel="noreferrer" className={sec} data-testid="mfa-link-iphone">iPhone</a>
      </div>
      <p className="text-xs text-slate-500">También sirve Microsoft Authenticator si ya la tienes.</p>
      <button onClick={onSiguiente} className={btn} data-testid="mfa-ya-instale">Ya la instalé <ChevronRight className="h-5 w-5" /></button>
    </div>
  );
}

function Escanear({ qr, onSiguiente, onAtras }) {
  const [verClave, setVerClave] = useState(false);
  const enlace = `otpauth://totp/Roge%20Human?secret=${qr.clave}&issuer=Roge`;
  return (
    <div className="space-y-4" data-testid="mfa-paso-escanear">
      <div className="flex gap-3"><ScanLine className="h-8 w-8 shrink-0 text-vino-700" /><p className="text-base text-slate-700">Abre la app, toca el botón <Plus className="inline h-4 w-4" /> y elige <strong>«Escanear un código QR»</strong>. Apunta la cámara a este cuadro:</p></div>
      <img src={qr.qr} alt="Código QR de verificación" className="mx-auto h-48 w-48 rounded-xl border-2 border-slate-200 bg-white p-1" data-testid="mfa-qr" />
      <a href={enlace} className={`${sec} md:hidden`} data-testid="mfa-abrir-app">Estoy en el celular: agregar directo a la app</a>
      <button type="button" onClick={() => setVerClave((v) => !v)} className="w-full text-sm font-medium text-vino-700 underline" data-testid="mfa-ver-clave">¿No puedes escanear? Escribe una clave</button>
      {verClave && (
        <div className="space-y-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
          <p>En la app elige <strong>«Ingresar clave de configuración»</strong>. Nombre: <strong>Roge</strong>. Clave:</p>
          <p className="break-all text-center font-mono text-base tracking-wider text-slate-900" data-testid="mfa-clave-manual">{qr.clave}</p>
          <button type="button" onClick={() => { navigator.clipboard.writeText(qr.clave); toast.success("Clave copiada"); }} className={sec} data-testid="mfa-copiar-clave"><Copy className="h-4 w-4" /> Copiar clave</button>
        </div>
      )}
      <button onClick={onSiguiente} className={btn} data-testid="mfa-ya-escanee">Ya aparece «Roge» en la app <ChevronRight className="h-5 w-5" /></button>
      <button type="button" onClick={onAtras} className="flex w-full items-center justify-center gap-1 text-sm text-slate-500"><ChevronLeft className="h-4 w-4" /> Atrás</button>
    </div>
  );
}

function IngresarCodigo({ titulo, codigo, setCodigo, onEnviar, busy, error, testid, btnTxt }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onEnviar(); }} className="space-y-4">
      <div className="flex gap-3"><KeyRound className="h-8 w-8 shrink-0 text-vino-700" /><p className="text-base text-slate-700">{titulo}</p></div>
      <input autoFocus inputMode="numeric" autoComplete="one-time-code" value={codigo} onChange={(e) => setCodigo(solo6(e.target.value))} placeholder="000000" className={inp} data-testid={testid} />
      <Error msg={error} />
      <button disabled={busy || codigo.length !== 6} className={btn} data-testid={`${testid}-enviar`}>{busy ? "Verificando…" : btnTxt}</button>
    </form>
  );
}

// Segundo paso del ingreso para administradores (TOTP): guía de configuración o verificación.
export function PasoMfa({ inicio, onCancel, onDone }) {
  const { aplicarSesion } = useAuth();
  const [paso, setPaso] = useState(1);
  const [qr, setQr] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [recup, setRecup] = useState("");
  const [usarRecup, setUsarRecup] = useState(false);
  const [restab, setRestab] = useState("");
  const [fin, setFin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mt = inicio.mfa_token;
  const llamar = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo completar. Intenta de nuevo."); } finally { setBusy(false); }
  };
  const generar = () => llamar(async () => { setQr((await api.post("/auth/mfa/configurar", { mfa_token: mt, codigo_restablecimiento: restab })).data); setPaso(2); });
  const activar = () => llamar(async () => setFin((await api.post("/auth/mfa/activar", { mfa_token: mt, codigo })).data));
  const verificar = () => llamar(async () => {
    const r = await api.post("/auth/mfa/verificar", usarRecup ? { mfa_token: mt, recuperacion: recup } : { mfa_token: mt, codigo });
    aplicarSesion(r.data.token, r.data.user); onDone();
  });
  if (fin) return <Codigos codigos={fin.codigos_recuperacion} onSeguir={() => { aplicarSesion(fin.token, fin.user); onDone(); }} />;
  const configurar = inicio.mfa !== "verificar";
  return (
    <div className="space-y-5" data-testid="paso-mfa">
      <div className="flex items-center gap-2 text-vino-800"><ShieldCheck className="h-5 w-5" /><p className="font-semibold">{configurar ? "Protege tu cuenta de administrador" : "Confirma que eres tú"}</p></div>
      {configurar && <Pasos actual={paso} />}
      {!configurar ? (
        !usarRecup ? (
          <IngresarCodigo titulo="Abre Google Authenticator en tu celular y escribe los 6 números que aparecen debajo de «Roge»." codigo={codigo} setCodigo={setCodigo} onEnviar={verificar} busy={busy} error={error} testid="mfa-codigo" btnTxt="Entrar" />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); verificar(); }} className="space-y-4">
            <p className="text-base text-slate-700">Escribe uno de tus códigos de emergencia (ejemplo: ABCD-1234).</p>
            <input autoFocus value={recup} onChange={(e) => setRecup(e.target.value)} placeholder="XXXX-XXXX" className={inp} data-testid="mfa-recuperacion" />
            <Error msg={error} />
            <button disabled={busy || !recup.trim()} className={btn} data-testid="mfa-verificar">Entrar</button>
          </form>
        )
      ) : paso === 1 ? (
        <>
          {inicio.requiere_codigo_restablecimiento && <input value={restab} onChange={(e) => setRestab(e.target.value)} placeholder="Código que te dio otro administrador" className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm" data-testid="mfa-codigo-restablecimiento" />}
          <Instalar onSiguiente={generar} />
          <Error msg={error} />
        </>
      ) : paso === 2 && qr ? (
        <Escanear qr={qr} onSiguiente={() => { setPaso(3); setError(""); }} onAtras={() => setPaso(1)} />
      ) : (
        <>
          <IngresarCodigo titulo="Escribe los 6 números que muestra la app debajo de «Roge». Si cambian, escribe los nuevos." codigo={codigo} setCodigo={setCodigo} onEnviar={activar} busy={busy} error={error} testid="mfa-codigo-activacion" btnTxt="Activar y continuar" />
          <button type="button" onClick={() => setPaso(2)} className="flex w-full items-center justify-center gap-1 text-sm text-slate-500" data-testid="mfa-volver-qr"><ChevronLeft className="h-4 w-4" /> Ver el código QR otra vez</button>
        </>
      )}
      {!configurar && (
        <>
          <button type="button" onClick={() => { setUsarRecup((v) => !v); setError(""); }} className="w-full text-sm font-medium text-vino-700 underline" data-testid="mfa-usar-recuperacion">{usarRecup ? "Usar el número de mi app" : "Perdí mi celular"}</button>
          <p className="text-xs text-slate-400">Sin celular ni códigos de emergencia, pide a otro administrador de HUMAN que restablezca tu verificación.</p>
        </>
      )}
      <button type="button" onClick={onCancel} className="w-full text-xs text-slate-400" data-testid="mfa-cancelar">Volver al inicio de sesión</button>
    </div>
  );
}
